import React, { useEffect, useState } from 'react'
import { Box, IconButton, Tooltip } from '@mui/material'
import { alpha } from '@mui/material/styles'
import CheckIcon from '@mui/icons-material/Check'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import BrokenImageIcon from '@mui/icons-material/BrokenImage'

import {
  resolveBackgroundImageUrl,
  type LibraryBackground,
} from '../services/backgroundLibrary'
import { backgroundEffectKey, type BackgroundEffect } from '../services/liveKitVideoBackground'

/**
 * One thumbnail, which has to fetch its own bytes.
 *
 * A plain `<img src="/api/backgrounds/:id/image">` cannot work here: an `<img>`
 * tag sends no `Authorization` header, and these routes are authorized. So each
 * tile resolves an object URL instead. Those are cached per id by the service,
 * so a grid of ten tiles is ten requests once and none thereafter.
 */
const LibraryThumbnail: React.FC<{ id: string }> = ({ id }) => {
  const [url, setUrl] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      const resolved = await resolveBackgroundImageUrl(id)
      if (cancelled) return
      if (resolved) setUrl(resolved)
      else setFailed(true)
    })()
    return () => {
      cancelled = true
    }
  }, [id])

  if (failed) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.disabled',
          bgcolor: 'action.hover',
        }}
      >
        <BrokenImageIcon fontSize="small" />
      </Box>
    )
  }

  return (
    <Box
      component="img"
      src={url ?? undefined}
      alt=""
      sx={{
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
        bgcolor: 'action.hover',
      }}
    />
  )
}

export interface BackgroundLibraryGridProps {
  backgrounds: LibraryBackground[]
  activeKey: string
  onSelect: (effect: BackgroundEffect) => void
  /** Omitted where the viewer may not remove these (a member on company images). */
  onDelete?: (background: LibraryBackground) => void
  /**
   * The effect key the user's camera is set to, when that is not `activeKey`:
   * on a surface that manages the set rather than choosing from it.
   */
  inUseKey?: string
  /**
   * The effect key still on screen while a newer pick loads. In use too until
   * that pick is in: if it fails, this one stays the selection.
   */
  showingKey?: string
  disabled?: boolean
  columns?: number
  /**
   * A tile is about to be picked: pointed at, focused or pressed. Lets the
   * caller start loading its image before the click lands.
   */
  onPrewarm?: (effect: BackgroundEffect) => void
}

/** Uploaded backgrounds — the caller's own, or their company's. */
export const BackgroundLibraryGrid: React.FC<BackgroundLibraryGridProps> = ({
  backgrounds,
  activeKey,
  onSelect,
  onDelete,
  inUseKey = activeKey,
  showingKey,
  disabled = false,
  columns = 3,
  onPrewarm,
}) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: `repeat(${columns}, 1fr)`,
      gap: 0.75,
    }}
  >
    {backgrounds.map((background) => {
      const effect: BackgroundEffect =
        background.kind === 'workspace'
          ? { kind: 'workspace', backgroundId: background.id }
          : { kind: 'custom', backgroundId: background.id }
      const key = backgroundEffectKey(effect)
      const selected = key === activeKey
      // The background in use is not deleted from under the camera: the user
      // moves off it first, rather than be held on an image that is gone.
      // Still on screen only until the newer pick is in. The user has already
      // picked another, so the advice is to wait, not to pick again.
      const stillShowing = key === showingKey && key !== inUseKey
      const inUse = key === inUseKey || stillShowing
      // Said on the tile too where the grid manages the set rather than
      // chooses from it: there "selected" is not what the camera uses. One only
      // still on screen says so, as its delete does: it is not the choice.
      const tileLabel = selected
        ? background.name
        : stillShowing
          ? `${background.name} (still showing)`
          : inUse
            ? `${background.name} (in use)`
            : background.name
      const deleteTooltip = stillShowing
        ? 'Still showing - wait until your new background is on'
        : inUse
          ? 'Pick another background first'
          : `Remove ${background.name}`
      const deleteLabel = stillShowing
        ? `Remove ${background.name}: still showing, wait until your new background is on`
        : inUse
          ? `Remove ${background.name}: in use, pick another background first`
          : `Remove ${background.name}`

      return (
        <Box key={background.id} sx={{ position: 'relative', '&:hover .bg-delete': { opacity: 1 } }}>
          <Box
            component="button"
            type="button"
            title={background.name}
            aria-label={tileLabel}
            aria-pressed={selected}
            disabled={disabled}
            data-testid={`background-effect-option-${key}`}
            onClick={() => onSelect(effect)}
            onPointerEnter={onPrewarm && (() => onPrewarm(effect))}
            onFocus={onPrewarm && (() => onPrewarm(effect))}
            onPointerDown={onPrewarm && (() => onPrewarm(effect))}
            sx={{
              display: 'block',
              width: '100%',
              p: 0,
              aspectRatio: '4 / 3',
              border: (t) => `2px solid ${selected ? t.palette.primary.main : 'transparent'}`,
              borderRadius: 1,
              overflow: 'hidden',
              cursor: disabled ? 'default' : 'pointer',
              background: 'transparent',
              opacity: disabled ? 0.5 : 1,
            }}
          >
            <LibraryThumbnail id={background.id} />
          </Box>

          {selected && (
            <CheckIcon
              fontSize="small"
              sx={{
                position: 'absolute',
                right: 2,
                bottom: 2,
                color: 'common.white',
                filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.8))',
                pointerEvents: 'none',
              }}
            />
          )}

          {onDelete && (
            <Box
              component="span"
              className="bg-delete"
              sx={{
                position: 'absolute',
                top: 2,
                right: 2,
                // Revealed on hover and on keyboard focus — hover-only would
                // put deletion out of reach of anyone not using a mouse.
                opacity: 0,
                '&:focus-within': { opacity: 1 },
                transition: 'opacity 150ms ease',
              }}
            >
              {/* The span carries the tooltip while the whole grid is disabled:
                  a natively disabled button fires no events. */}
              <Tooltip title={deleteTooltip}>
                <span>
                  <IconButton
                    size="small"
                    // In use is not natively disabled: that would take it out
                    // of the tab order, and keyboard and screen-reader users
                    // would never learn why it cannot be removed.
                    disabled={disabled}
                    aria-disabled={inUse || undefined}
                    data-testid={`background-delete-${background.id}`}
                    aria-label={deleteLabel}
                    onClick={() => {
                      if (!inUse) onDelete(background)
                    }}
                    sx={{
                      // A scrim over an arbitrary photo, from the theme rather
                      // than a fixed colour.
                      bgcolor: (t) => alpha(t.palette.common.black, 0.55),
                      color: 'common.white',
                      '&:hover': { bgcolor: (t) => alpha(t.palette.common.black, 0.75) },
                      '&[aria-disabled="true"]': {
                        color: (t) => alpha(t.palette.common.white, 0.5),
                        cursor: 'not-allowed',
                      },
                    }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          )}
        </Box>
      )
    })}
  </Box>
)

export default BackgroundLibraryGrid
